export const ms = {
  sec: 1000,
  min: 1000 * 60,
  hour: 1000 * 60 * 60,
  day: 1000 * 60 * 60 * 24,
  week: 1000 * 60 * 60 * 24 * 7,
  month: 1000 * 60 * 60 * 24 * 30,
  year: 1000 * 60 * 60 * 24 * 365,
  format: formatDuration,
}

export const bytes = {
  kb: 1024,
  mb: 1024 * 1024,
  gb: 1024 * 1024 * 1024,
  tb: 1024 * 1024 * 1024 * 1024,
  getSize,
  getRoughObjectSize,
  format: formatBytes,
}

// decimals: 2 -> 1500 -> 1.46kb, decimals: 0 -> 1500 -> 1kb
function formatBytes(bytes?: number, opts?: { decimals?: number; capitalize?: boolean }) {
  if (!bytes) return '0 b'

  const { decimals = 2, capitalize = false } = opts ?? {}
  const k = 1024 //  factor by which byte sizes increase
  const { log } = Math // to avoid interfering with searching for (console).log
  const i = Math.floor(log(bytes) / log(k)) //  if bytes = 1500, i = 1, selected unit = 'KB'.

  const result = `${Number.parseFloat((bytes / k ** i).toFixed(decimals))} ${sizes[i]}`

  return capitalize ? result.toUpperCase() : result
}
const sizes = ['b', 'kb', 'mb', 'gb', 'tb']

function formatDuration(ms: number) {
  if (ms === 0) return '0 ms'

  let result = ''
  let remainingMs = ms
  let unitCount = 0

  // Loop through units from largest to smallest, except milliseconds
  for (let i = units.length - 1; i >= 0; i--) {
    const unitValue = Math.floor(remainingMs / divisors[i])
    remainingMs -= unitValue * divisors[i]

    if (unitValue > 0) {
      // For durations less than a minute (seconds and milliseconds)
      if (i === 1 && unitCount === 0) {
        // If it's in seconds and no larger unit has been processed
        if (remainingMs > 0) {
          // If there are remaining milliseconds, format as decimal seconds
          const totalSeconds = unitValue + remainingMs / 1000
          return `${totalSeconds.toFixed(1)}s`
        }
        return `${unitValue}s` // Just seconds, no milliseconds remaining
      }

      result += `${unitValue}${units[i]} `
      unitCount++

      if (unitCount === 2) break // Only include the first two significant units
    }
  }

  return result.trim()
}

const units = ['ms', 's', 'min', 'h', 'days']
const divisors = [1, 1000, 60000, 3600000, 86400000]

function getSize(d: unknown) {
  const data = typeof d === 'string' ? d : JSON.stringify(d)
  return formatBytes(new TextEncoder().encode(data).length)
}

export function getRoughObjectSize(object: unknown) {
  const processedObjects = new Set()
  const stack = [object]
  let bytes = 0

  while (stack.length) {
    const value = stack.pop()

    if (typeof value === 'boolean') {
      bytes += 4
    } else if (typeof value === 'string') {
      bytes += value.length * 2
    } else if (typeof value === 'number') {
      bytes += 8
    } else if (typeof value === 'object' && !processedObjects.has(value)) {
      processedObjects.add(value)
      bytes += sizeObjectAndQueueChildren(value, stack)
    }
  }
  return bytes
}

function sizeObjectAndQueueChildren(value: object | null, stack: unknown[]): number {
  if (Array.isArray(value)) {
    for (const element of value) stack.push(element)
    return 0
  }
  if (value === null) return 0
  // Byte containers are sized, never walked: Object.entries on a typed array or
  // Buffer allocates a key string and a pair per byte, so a megabyte upload costs
  // hundreds of megabytes of heap. An ArrayBuffer or DataView has no entries at all.
  if (value instanceof ArrayBuffer || ArrayBuffer.isView(value)) return value.byteLength

  let bytes = 0
  for (const [key, propertyValue] of Object.entries(value as Record<string, unknown>)) {
    bytes += key.length * 2
    stack.push(propertyValue)
  }
  return bytes
}
